import { FC, ReactNode, useMemo, useState } from "react";
import {
	Box,
	Button,
	CircularProgress,
	IconButton,
	LinearProgress,
	MenuItem,
	OutlinedInput,
	Paper,
	Select,
	Stack,
	Typography,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useQuery } from "@tanstack/react-query";
import { useUserStore } from "../../../../../stores/user.store";
import { useCurrencySetting } from "../../../../../hooks/useSetting";
import useEnrollmentSummaryConfigurable from "../../../../../hooks/useEnrollmentSummaryConfigurable";
import { useGetProviderSitesByOUId } from "../../../../../hooks/useBeneficiaries";
import {
	EnrollmentSummaryConfigurableFilters,
	EnrollmentSummaryConfigurableRow,
	VisitDetailResponse,
	reportsService,
} from "../../../../../services/reports.service";
import { triggerDownload } from "../../../../../utils/xlsx-export/build-workbook";
import { buildEnrollmentSummaryWorkbook } from "../../../../../utils/xlsx-export/enrollment-summary-workbook";
import { useAppTheme } from "../../../../../utils/theme";
import { BasicDialog } from "../../../../../components/BasicDialog";
import {
	FilterField,
	NoProgramCard,
	ProgramFilterField,
	ReportTabProps,
	programNameOf,
	programOrgUnitIds,
} from "../common";

const STATUS_OPTIONS = ["ACTIVE", "COMPLETED", "DISENROLLED", "PAUSED"];

const titleCase = (s: string): string =>
	s ? s.charAt(0) + s.slice(1).toLowerCase() : s;

const formatPeriod = (start: string | null, end: string | null): string => {
	const s = start ? dayjs(start).format("YYYY-MM-DD") : "—";
	const e = end ? dayjs(end).format("YYYY-MM-DD") : "active";
	return `${s} – ${e}`;
};

interface DrillTarget {
	cin: string;
	name: string | null;
	site: string | null;
}

interface ColDef {
	key: string;
	label: string;
	align: "left" | "right" | "center";
	result?: boolean;
	render: (r: EnrollmentSummaryConfigurableRow) => ReactNode;
}

const EnrollmentSummaryTab: FC<ReportTabProps> = ({
	programId,
	programs,
	onProgramChange,
}) => {
	const theme = useAppTheme();
	const { userInfo } = useUserStore();
	const accountId = userInfo?.accountId ?? "";
	const currencyMask = useCurrencySetting()?.mask ?? "$";
	const programName = programNameOf(programs, programId);

	const [status, setStatus] = useState<string>("");
	const [orgUnitIds, setOrgUnitIds] = useState<string[]>([]);
	const [provider, setProvider] = useState<string>("");
	const [pageNumber, setPageNumber] = useState<number>(0);
	const [pageSize] = useState<number>(25);
	const [drill, setDrill] = useState<DrillTarget | null>(null);

	// SIMS-7595: the OU ids the user is assigned for this program. Used to scope
	// the OU dropdown AND to constrain the report to the user's OUs when none is
	// explicitly selected, so first-load rows never include unassigned OUs.
	const assignedOuIds = programOrgUnitIds(userInfo?.programs, programId);

	const { data, isLoading, isFetching } = useEnrollmentSummaryConfigurable({
		accountId,
		programId,
		status: status || undefined,
		orgUnitIds:
			(orgUnitIds.length ? orgUnitIds : assignedOuIds).join(",") || undefined,
		providerId: provider || undefined,
		pageNumber,
		pageSize,
	});

	// SIMS-7595: the "Org units" dropdown lists only the OUs the user is assigned
	// for this program (mirroring the Beneficiary Report), not the program-wide
	// data.availableSites which leaks OUs the user isn't assigned to. Resolve the
	// user's assigned OUs → their provider sites and use those as the options.
	const { data: providerSites } = useGetProviderSitesByOUId({
		ouIds: assignedOuIds,
		queryKey: "sims-reports-provider-sites",
	});

	const columns = useMemo(() => data?.columns ?? [], [data]);
	const sites = providerSites ?? [];
	const providers = data?.availableProviders ?? [];
	const total = data?.pagination.total ?? 0;

	// Design tokens → theme palette (driven by the account's themeColors).
	const c = {
		band: theme.palette.surface.main,
		border: theme.palette.outline.light,
		divider: theme.palette.outline.light,
		label: theme.palette.textSecondary.main,
		muted: theme.palette.textSecondary.main,
		ink: theme.palette.textPrimary.main,
		accent: theme.palette.primary.main,
		accentBand: theme.palette.surface.dark,
		zebra: theme.palette.surface.main,
		resultHeadBg: theme.palette.surface.dark,
		resultCellBg: theme.palette.surface.main,
		error: theme.palette.error.main,
	};

	const money = (n: number): string => `${currencyMask}${(n ?? 0).toFixed(2)}`;

	const statusStyle = (s: string): { bg: string; color: string } => {
		const u = (s ?? "").toUpperCase();
		if (u === "ACTIVE") {
			return {
				bg: theme.palette.secondary.light,
				color: theme.palette.secondary.dark,
			};
		}
		if (u === "COMPLETED") {
			return {
				bg: theme.palette.surface.dark,
				color: theme.palette.primary.main,
			};
		}
		if (u === "PAUSED") {
			return {
				bg: theme.palette.warning.light,
				color: theme.palette.warning.dark,
			};
		}
		return { bg: theme.palette.surface.main, color: c.muted };
	};

	const resultStyle = (value: string): { bg: string; color: string } => {
		const s = (value ?? "").toLowerCase();
		if (/(unexcused|positive|missed)/.test(s)) {
			return { bg: theme.palette.error.light, color: theme.palette.error.dark };
		}
		if (/(absent|excus)/.test(s)) {
			return {
				bg: theme.palette.warning.light,
				color: theme.palette.warning.dark,
			};
		}
		if (/(negative|attend)/.test(s)) {
			return {
				bg: theme.palette.secondary.light,
				color: theme.palette.secondary.dark,
			};
		}
		return { bg: c.band, color: c.muted };
	};

	const Pill: FC<{ bg: string; color: string; children: ReactNode }> = ({
		bg,
		color,
		children,
	}) => (
		<Box
			component="span"
			sx={{
				display: "inline-block",
				bgcolor: bg,
				color,
				fontSize: 13,
				fontWeight: 600,
				px: 1.5,
				py: 0.5,
				borderRadius: "9999px",
				whiteSpace: "nowrap",
			}}
		>
			{children}
		</Box>
	);

	// Column descriptors — header and body render from the SAME list so the
	// horizontally-scrolling table never misaligns. CIN is first (per request).
	const columnDefs = useMemo<ColDef[]>(() => {
		const fmt = (n: number): string => `${currencyMask}${(n ?? 0).toFixed(2)}`;
		const fixed: ColDef[] = [
			{
				key: "cin",
				label: "CIN",
				align: "left",
				render: (r) =>
					r.cin ? (
						<Box
							component="span"
							onClick={() =>
								setDrill({
									cin: r.cin as string,
									name: r.beneficiaryName,
									site: r.site,
								})
							}
							sx={{
								color: c.accent,
								fontWeight: 600,
								textDecoration: "underline",
								cursor: "pointer",
							}}
						>
							{r.cin}
						</Box>
					) : (
						"—"
					),
			},
			{
				key: "name",
				label: "Name",
				align: "left",
				render: (r) => r.beneficiaryName ?? "—",
			},
			{
				key: "site",
				label: "Site",
				align: "left",
				render: (r) => r.site ?? "—",
			},
			{
				key: "provider",
				label: "Provider",
				align: "left",
				render: (r) => r.provider ?? "—",
			},
			{
				key: "npi",
				label: "NPI",
				align: "left",
				render: (r) => (
					<Box component="span" sx={{ color: c.muted }}>
						{r.npi ?? "—"}
					</Box>
				),
			},
			{
				key: "period",
				label: "Enrollment period",
				align: "left",
				render: (r) => formatPeriod(r.startDate, r.endDate),
			},
			{
				key: "status",
				label: "Status",
				align: "left",
				render: (r) => {
					const st = statusStyle(r.status);
					return (
						<Pill bg={st.bg} color={st.color}>
							{titleCase(r.status)}
						</Pill>
					);
				},
			},
			{
				key: "payerPlanName",
				label: "Payer plan name",
				align: "left",
				render: (r) => (
					<Box
						component="span"
						sx={{ color: r.payerPlanName ? c.ink : c.muted }}
					>
						{r.payerPlanName ?? "—"}
					</Box>
				),
			},
			{
				key: "payerPlanNumber",
				label: "Payer plan #",
				align: "left",
				render: (r) => (
					<Box
						component="span"
						sx={{ color: r.payerPlanNumber ? c.ink : c.muted }}
					>
						{r.payerPlanNumber ?? "—"}
					</Box>
				),
			},
		];
		const result: ColDef[] = columns.map((col) => ({
			key: `rc_${col.key}`,
			label: col.label,
			align: "center" as const,
			result: true,
			render: (r) => r.resultCounts?.[col.key] ?? 0,
		}));
		const incentives: ColDef[] = [
			{
				key: "incentiveEarned",
				label: "Inc. earned",
				align: "right",
				render: (r) => fmt(r.incentiveEarned),
			},
			{
				key: "incentiveReceived",
				label: "Inc. received",
				align: "right",
				render: (r) => fmt(r.incentiveDisbursed),
			},
			{
				key: "bankBalance",
				label: "Bank bal.",
				align: "right",
				render: (r) => fmt(r.bankBalance),
			},
		];
		return [...fixed, ...result, ...incentives];
		// eslint-disable-next-line
	}, [columns, currencyMask]);

	// In-place drill-through: that enrollment's visits, by CIN.
	const drillQuery = useQuery<VisitDetailResponse>({
		queryKey: ["es-drill-visits", accountId, programId, drill?.cin ?? ""],
		queryFn: () =>
			drill
				? reportsService.getVisitDetail({
						accountId,
						programId,
						cin: drill.cin,
						pageNumber: 0,
						pageSize: 100,
					})
				: Promise.reject(new Error("no drill target")),
		enabled: Boolean(drill),
		staleTime: 1000 * 60 * 5,
	});

	const [exporting, setExporting] = useState<"" | "excel" | "pdf">("");

	const contextLine = (): string =>
		[
			programName,
			orgUnitIds.length
				? orgUnitIds
						.map((id) => sites.find((s) => s.id === id)?.name ?? id)
						.join(", ")
				: "All org units",
			status ? titleCase(status) : "All statuses",
			"lifetime totals",
		]
			.filter(Boolean)
			.join(" · ");

	const exportFilters = (): EnrollmentSummaryConfigurableFilters => ({
		accountId,
		programId,
		status: status || undefined,
		// SIMS-7595: mirror the on-screen fetch — fall back to the user's assigned
		// OUs when none is selected, so the Excel/PDF export is scoped to the same
		// OUs as the screen and never leaks unassigned-OU rows.
		orgUnitIds:
			(orgUnitIds.length ? orgUnitIds : assignedOuIds).join(",") || undefined,
		providerId: provider || undefined,
		filterSummary: contextLine(),
	});

	// SIMS-7588: build the .xlsx in the browser (exceljs) from JSON data instead
	// of downloading a binary body through API Gateway/CloudFront, which arrived
	// as undecoded base64 and could not be opened in Excel. Same fix as the Visit
	// Detail export (visit-detail-workbook.ts).
	const handleExcel = async (): Promise<void> => {
		setExporting("excel");
		try {
			const { blob, filename } = await buildEnrollmentSummaryWorkbook({
				...exportFilters(),
				programName,
				currencyMask,
			});
			triggerDownload({ blob, filename });
		} finally {
			setExporting("");
		}
	};

	const handlePdf = async (): Promise<void> => {
		setExporting("pdf");
		try {
			const { columns: cols, rows: allRows } =
				await reportsService.fetchAllEnrollmentSummaryConfigurable(
					exportFilters(),
				);
			const fmt = (n: number): string =>
				`${currencyMask}${(n ?? 0).toFixed(2)}`;
			const doc = new jsPDF("landscape", "pt", "a4");
			doc.setFontSize(14);
			doc.text("Enrollment Summary Report", 40, 40);
			const ctx = contextLine();
			if (ctx) {
				doc.setFontSize(9);
				doc.text(`Filters: ${ctx}`, 40, 56);
			}
			const head = [
				[
					"CIN",
					"Name",
					"Site",
					"Provider",
					"NPI",
					"Enrollment period",
					"Status",
					"Payer plan name",
					"Payer plan #",
					...cols.map((col) => col.label),
					"Inc. earned",
					"Inc. received",
					"Bank bal.",
				],
			];
			const body = allRows.map((r) => [
				r.cin ?? "—",
				r.beneficiaryName ?? "—",
				r.site ?? "—",
				r.provider ?? "—",
				r.npi ?? "—",
				formatPeriod(r.startDate, r.endDate),
				titleCase(r.status),
				r.payerPlanName ?? "—",
				r.payerPlanNumber ?? "—",
				...cols.map((col) => String(r.resultCounts?.[col.key] ?? 0)),
				fmt(r.incentiveEarned),
				fmt(r.incentiveDisbursed),
				fmt(r.bankBalance),
			]);
			autoTable(doc, { startY: 68, head, body, styles: { fontSize: 7 } });
			doc.save("enrollment_summary.pdf");
		} finally {
			setExporting("");
		}
	};

	const rows = data?.rows ?? [];
	const totalPages = Math.max(1, Math.ceil(total / pageSize));

	return (
		<Stack spacing={3} sx={{ minWidth: 0 }}>
			{/* Page header card */}
			<Paper
				variant="outlined"
				sx={{
					borderColor: c.border,
					borderRadius: 3,
					p: 3,
					display: "flex",
					alignItems: "flex-start",
					justifyContent: "space-between",
					flexWrap: "wrap",
					gap: 2,
				}}
			>
				<Box>
					<Typography sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1.1 }}>
						Enrollment Summary Report
					</Typography>
					<Typography sx={{ fontSize: 15, color: c.muted, mt: 0.75 }}>
						Reports / Enrollment summary
					</Typography>
					<Typography sx={{ fontSize: 15, color: c.muted, mt: 1 }}>
						One row per enrollment · lifetime totals · result columns driven by
						program event configuration
					</Typography>
				</Box>
				<Stack direction="row" spacing={2}>
					<Button
						variant="outlined"
						startIcon={
							exporting === "excel" ? (
								<CircularProgress size={16} color="inherit" />
							) : (
								<FileDownloadOutlinedIcon />
							)
						}
						disabled={exporting !== "" || !programId}
						onClick={handleExcel}
					>
						{exporting === "excel" ? "Exporting…" : "Export to Excel"}
					</Button>
					<Button
						variant="outlined"
						startIcon={
							exporting === "pdf" ? (
								<CircularProgress size={16} color="inherit" />
							) : (
								<FileDownloadOutlinedIcon />
							)
						}
						disabled={exporting !== "" || !programId}
						onClick={handlePdf}
					>
						{exporting === "pdf" ? "Exporting…" : "Export to PDF"}
					</Button>
				</Stack>
			</Paper>

			{/* Filter bar card */}
			<Paper
				variant="outlined"
				sx={{ borderColor: c.border, borderRadius: 3, p: 3 }}
			>
				<Box
					sx={{
						display: "grid",
						// Wrap filters instead of overflowing the page on narrow screens:
						// auto-fit + minmax keeps each control ≥220px, then wraps to a new
						// row before anything is clipped off the left edge.
						gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
						gap: 3,
					}}
				>
					<ProgramFilterField
						programId={programId}
						programs={programs}
						onProgramChange={(id) => {
							onProgramChange(id);
							setPageNumber(0);
							setDrill(null);
							// Org units and provider are program-specific — clear them so
							// a stale selection from the previous program isn't sent for
							// a different program.
							setOrgUnitIds([]);
							setProvider("");
						}}
						labelColor={c.label}
						mutedColor={c.muted}
						errorColor={c.error}
					/>
					<FilterField label="Enrollment status" labelColor={c.label}>
						<Select
							size="small"
							displayEmpty
							value={status}
							input={<OutlinedInput />}
							onChange={(e) => {
								setStatus(e.target.value);
								setPageNumber(0);
							}}
							renderValue={(v) =>
								v ? (
									titleCase(v)
								) : (
									<Typography sx={{ color: c.muted }}>All statuses</Typography>
								)
							}
						>
							<MenuItem value="">All statuses</MenuItem>
							{STATUS_OPTIONS.map((s) => (
								<MenuItem key={s} value={s}>
									{titleCase(s)}
								</MenuItem>
							))}
						</Select>
					</FilterField>
					<FilterField label="Org units" labelColor={c.label}>
						<Select
							size="small"
							multiple
							displayEmpty
							value={orgUnitIds}
							input={<OutlinedInput />}
							onChange={(e) => {
								const v = e.target.value;
								setOrgUnitIds(typeof v === "string" ? v.split(",") : v);
								setPageNumber(0);
							}}
							renderValue={(selected) =>
								selected.length ? (
									selected
										.map((id) => sites.find((s) => s.id === id)?.name ?? id)
										.join(", ")
								) : (
									<Typography sx={{ color: c.muted }}>All org units</Typography>
								)
							}
						>
							{sites.map((s) => (
								<MenuItem key={s.id} value={s.id}>
									{s.name ?? s.id}
								</MenuItem>
							))}
						</Select>
					</FilterField>
					<FilterField label="Provider" labelColor={c.label}>
						<Select
							size="small"
							displayEmpty
							value={provider}
							input={<OutlinedInput />}
							onChange={(e) => {
								setProvider(e.target.value);
								setPageNumber(0);
							}}
							renderValue={(v) =>
								v ? (
									(providers.find((p) => p.id === v)?.name ?? v)
								) : (
									<Typography sx={{ color: c.muted }}>All providers</Typography>
								)
							}
						>
							<MenuItem value="">All providers</MenuItem>
							{providers.map((p) => (
								<MenuItem key={p.id} value={p.id}>
									{p.name ?? p.id}
								</MenuItem>
							))}
						</Select>
					</FilterField>
				</Box>
			</Paper>

			{/* Reserve the bar's height so the table doesn't jump on refetch. */}
			<Box sx={{ height: 4 }}>
				{(isLoading || isFetching) && <LinearProgress />}
			</Box>

			{!programId ? (
				<NoProgramCard borderColor={c.border} mutedColor={c.muted} />
			) : !isLoading && !isFetching && rows.length === 0 ? (
				/* Empty state */
				<Paper
					variant="outlined"
					sx={{
						borderColor: c.border,
						borderRadius: 3,
						py: 9,
						px: 3,
						textAlign: "center",
					}}
				>
					<TableRowsOutlinedIcon sx={{ fontSize: 48, color: c.muted }} />
					<Typography sx={{ fontSize: 20, mt: 1 }}>
						No records found.
					</Typography>
					<Typography sx={{ fontSize: 15, color: c.muted, mt: 0.5 }}>
						No enrollments match the current filters.
					</Typography>
				</Paper>
			) : (
				/* Summary table */
				<Paper
					variant="outlined"
					sx={{
						borderColor: c.border,
						borderRadius: 3,
						overflow: "hidden",
						minWidth: 0,
					}}
				>
					<Box sx={{ bgcolor: c.band, px: 3, py: 2 }}>
						<Typography
							sx={{
								fontSize: 13,
								fontWeight: 700,
								letterSpacing: "0.08em",
								color: c.muted,
							}}
						>
							ENROLLMENT RESULTS
						</Typography>
						<Typography sx={{ fontSize: 14, color: c.muted, mt: 0.5 }}>
							{contextLine()}
						</Typography>
					</Box>

					<Box sx={{ width: "100%", maxWidth: "100%", overflowX: "auto" }}>
						<Box
							component="table"
							sx={{
								borderCollapse: "collapse",
								width: "100%",
								minWidth: 1320,
								fontSize: 16,
							}}
						>
							<Box component="thead">
								<Box
									component="tr"
									sx={{
										textAlign: "left",
										color: c.muted,
										borderBottom: `1px solid ${c.divider}`,
									}}
								>
									{columnDefs.map((col) => (
										<Box
											key={col.key}
											component="th"
											sx={{
												fontSize: 12,
												fontWeight: 600,
												letterSpacing: "0.05em",
												p: "14px 16px",
												whiteSpace: "nowrap",
												textAlign: col.align,
												textTransform: "uppercase",
												color: col.result ? c.accent : c.muted,
												bgcolor: col.result ? c.resultHeadBg : "transparent",
											}}
										>
											{col.label}
										</Box>
									))}
								</Box>
							</Box>
							<Box component="tbody">
								{rows.map((r, idx) => (
									<Box
										component="tr"
										key={r.enrollmentId}
										sx={{
											borderBottom: `1px solid ${c.divider}`,
											bgcolor: idx % 2 === 1 ? c.zebra : "transparent",
											"&:hover": { bgcolor: theme.palette.surface.main },
										}}
									>
										{columnDefs.map((col) => (
											<Box
												key={col.key}
												component="td"
												sx={{
													p: "16px",
													whiteSpace: "nowrap",
													textAlign: col.align,
													bgcolor: col.result ? c.resultCellBg : "transparent",
													fontVariantNumeric:
														col.align !== "left" ? "tabular-nums" : undefined,
												}}
											>
												{col.render(r)}
											</Box>
										))}
									</Box>
								))}
							</Box>
						</Box>
					</Box>

					{/* Footer: count + pager on one line */}
					<Stack
						direction="row"
						alignItems="center"
						justifyContent="space-between"
						sx={{
							px: 3,
							py: 1.5,
							borderTop: `1px solid ${c.divider}`,
							bgcolor: c.band,
						}}
					>
						<Typography sx={{ fontSize: 15, color: c.muted }}>
							{total} enrollments
						</Typography>
						<Stack direction="row" spacing={1} alignItems="center">
							<IconButton
								size="small"
								disabled={pageNumber === 0}
								onClick={() => setPageNumber((p) => Math.max(0, p - 1))}
								aria-label="Previous page"
							>
								<ChevronLeftIcon fontSize="small" />
							</IconButton>
							<Typography sx={{ fontSize: 15, color: c.muted }}>
								Page {pageNumber + 1} of {totalPages}
							</Typography>
							<IconButton
								size="small"
								disabled={pageNumber + 1 >= totalPages}
								onClick={() =>
									setPageNumber((p) => Math.min(totalPages - 1, p + 1))
								}
								aria-label="Next page"
							>
								<ChevronRightIcon fontSize="small" />
							</IconButton>
						</Stack>
					</Stack>
				</Paper>
			)}

			{/* CIN drill-through opens the enrollment's visits in a modal, so the
			    summary table (query, filters, page) stays intact underneath. */}
			{drill && (
				<BasicDialog
					title={`Visit detail${drill.cin ? ` · CIN ${drill.cin}` : ""}`}
					onCancel={() => setDrill(null)}
					actions={
						<Button variant="outlined" onClick={() => setDrill(null)}>
							Close
						</Button>
					}
					sx={{
						"& .MuiDialog-paper": { maxWidth: 1000, width: "100%" },
					}}
				>
					{(drill.name || drill.site) && (
						<Typography sx={{ fontSize: 14, color: c.muted, mb: 1.5 }}>
							{[drill.name, drill.site].filter(Boolean).join(" · ")}
						</Typography>
					)}
					{drillQuery.isLoading ? (
						<LinearProgress />
					) : (drillQuery.data?.rows ?? []).length === 0 ? (
						<Box sx={{ p: 4, textAlign: "center" }}>
							<Typography sx={{ color: c.muted }}>
								No visits recorded for this enrollment yet.
							</Typography>
						</Box>
					) : (
						<Box sx={{ overflowX: "auto" }}>
							<Box
								component="table"
								sx={{ width: "100%", borderCollapse: "collapse", fontSize: 15 }}
							>
								<Box component="thead">
									<Box
										component="tr"
										sx={{
											textAlign: "left",
											color: c.muted,
											borderBottom: `1px solid ${c.divider}`,
										}}
									>
										{[
											"Visit #",
											"Visit date",
											"Result",
											"Incentive earned",
											"Provider / CM coordinator",
											"Site",
										].map((h, i) => (
											<Box
												key={h}
												component="th"
												sx={{
													fontSize: 12,
													fontWeight: 600,
													letterSpacing: "0.05em",
													p: "12px 16px",
													textTransform: "uppercase",
													textAlign: i === 3 ? "right" : "left",
													whiteSpace: "nowrap",
												}}
											>
												{h}
											</Box>
										))}
									</Box>
								</Box>
								<Box component="tbody">
									{(drillQuery.data?.rows ?? []).map((v) => {
										const rs = resultStyle(v.result ?? "");
										return (
											<Box
												component="tr"
												key={v.udtResultId}
												sx={{ borderBottom: `1px solid ${c.divider}` }}
											>
												<Box component="td" sx={{ p: "12px 16px" }}>
													{v.visitNumber ?? "—"}
												</Box>
												<Box component="td" sx={{ p: "12px 16px" }}>
													{v.visitDate
														? dayjs(v.visitDate).format("YYYY-MM-DD")
														: "—"}
												</Box>
												<Box component="td" sx={{ p: "12px 16px" }}>
													{v.result ? (
														<Pill bg={rs.bg} color={rs.color}>
															{v.result}
														</Pill>
													) : (
														"—"
													)}
												</Box>
												<Box
													component="td"
													sx={{ p: "12px 16px", textAlign: "right" }}
												>
													{money(v.earned)}
												</Box>
												<Box component="td" sx={{ p: "12px 16px" }}>
													{v.provider ?? "—"}
												</Box>
												<Box component="td" sx={{ p: "12px 16px" }}>
													{v.orgUnit ?? "—"}
												</Box>
											</Box>
										);
									})}
								</Box>
							</Box>
						</Box>
					)}
				</BasicDialog>
			)}
		</Stack>
	);
};

export default EnrollmentSummaryTab;
